import Vue from 'vue'
import Vuex from 'vuex'
import sim from "@/assets/data/leadership.json"
import shuffle from '@/assets/js/shuffle.js'
import router from '@/router/index.js'

Vue.use(Vuex);

const _flr = Math.floor
const _rdm = Math.random

const __debugSelections = false
const __shuffleOptions = true

const _sort = ( a, b ) => a < b ? -1 : a > b ? 1 : 0
const _reduce = ( a, b ) => a + b

const _shuffle = ( a ) => {
    let i = a.length
    let r
    while( i != 0 ){
        r = _flr( _rdm() * i)
        i--
        [ a[ i ], a[ r ] ] = [ a[ r ], a[ i ] ]
    }
    return a
}

// setup the selection map that holds
// a list of answers to the questions
function _selected(){
    return sim.actions.map( action => {
        return action.decisions.map( decision => {
            return decision.options.map( option => false )
        } )
    } )
}

// if debugging, populate the selection map
// with answers so that we don't need to walk through the app
function _debugSelected(){
    return sim.actions.map( action => {
        return action.decisions.map( decision => {
            let ct = decision.typeMeta.count
            return _shuffle( decision.options.map( option => {
                ct -= 1
                return ct >= 0
            } ) )
        } )
    } )
}

function _submitted(){
    return sim.actions.map( a => __debugSelections )
}

function _actions(){
    return !__shuffleOptions ? sim.actions : sim.actions.map( action => {
        action.decisions = action.decisions.map( decision => {
            if(decision.type === 'radio') return decision
            decision.options = _shuffle( decision.options )
            return decision
        } )
        return action
    } )
}

const store = new Vuex.Store({
    state : {
        dimensions     : sim.dimensions,
        scoring        : sim.scoring,
        introduction   : sim.introduction,
        gettingStarted : sim.gettingStarted,
        meta           : sim.meta,
        actions   : _actions(),
        submitted : _submitted(),
        selected  : __debugSelections ? _debugSelected() : _selected(),
        
        userData : {
            email     : '',
            startTime : '',
            endTime   : '',
            userType  : '',
            simName: sim.introduction.shortname,    
        },
        results : {},
        debug   : false,
        version : process.env.VUE_APP_VERSION || 'x'
    },
    mutations: {
        submitAction(state, i){
            state.submitted.splice(i, 1, true)
        },
        toggleSelection(state, {i, j, k, val}){
            const update = state.selected[i][j].slice()
            update.splice(k, 1, val)
            state.selected[i].splice(j, 1, update)
        },
        reset(state){
            state.selected  = __debugSelections ? _debugSelected() : _selected()
            state.submitted = _submitted()

            // reset bar chart to original value- communication sim only
            if (state.meta.scores !== null) { // make sure it exists in json
                Object.keys(state.meta.scores).forEach( entry => {
                    state.meta.scores[entry].userScore = state.meta.scores[entry].baseline
                })
            }
        },
        setEmail(state, email){
            const domain = email.split('@').pop()
            // const clientDomains = ['cvshealth.com', 'aetna.com', 'coramhc.com', 'omnicare.com', 'meritain.com']
            const internalDomains = [ 'insight-experience.com', 'insightxp.com', 'thebiggerdesign.com', 'epiccreature.com' ]
            state.userData.email = email
            state.userData.startTime = Date.now()
            state.userData.userType = internalDomains.includes(domain) ? "test" : "production"
        },
        setEndTime(state){
            state.userData.endTime = Date.now()
        },
        toggleDebug(state){
            state.debug = !state.debug
        },
        postResults(state, results){
            state.results = results
        }
    },
    actions: {
        toggleDebug({commit}){
            commit('toggleDebug')
        },
        setEmail({commit}, email ){
            commit('setEmail', email)
        },
        setEndTime({commit}) {
            commit('setEndTime');
            // this.$gtag.event('complete', { course: 'Scanning' })
        },
        toggleSelection({commit, state, getters}, p){
            const i = getters.actionIndex
            const j = p.i
            const k = p.j
            const val = !state.selected[i][j][k]
            if ( getters.areSelectionsMaxed(j) && val ) {
                return false
            }
            commit('toggleSelection', {i, j, k, val})
        },
        goToNextAction({commit}){
            commit('goToNextAction')
        },
        submitAction({commit, getters}){
            commit('submitAction', getters.actionIndex)
        },
        reset({state, commit}){
            commit('reset')
            router.push({ name : "overview" }) 
        }
    },
    getters: {
        getState          : state => state,
        getUserType       : state => state.userData.userType,
        getSimName        : state => state.userData.simName,
        getMeta           : ({meta})           => meta,
        getVersion        : ({version})        => version,
        getSelected       : ({selected})       => selected,
        getScoring        : ({scoring})        => scoring,
        getIntroduction   : ({introduction})   => introduction,
        getGettingStarted : ({gettingStarted}) => gettingStarted,
        getEmail          : ({userData})       => userData.email,

        getDecisionCount  : ({submitted})      => submitted.length,
        getDecisionRound  : ({submitted})      => submitted.filter(s => s).length,
        isSimComplete     : ({submitted})      => submitted.filter(s => s).length === submitted.length,

        currentAction     : ({actions}, {actionIndex}) => actions[actionIndex],
        currentFeedback   : ({actions}, {actionIndex}) => actions[actionIndex - 1] || "",
        getInstructions   : ({actions}, {actionIndex}) => actions[actionIndex].instructions,

        actionIndex({submitted}){
            for( let i = 0; i < submitted.length; i++ ){
                if(!submitted[i]){ return i }
            }
            return submitted.length
        },
        getActionBySlug({actions}){
            return slug => actions.filter(a => a.slug === slug)[0]
        },
        getInvestigationBySlug({actions}){
            return (actionSlug, itemSlug) => {
                return actions
                    .filter(a => a.slug === actionSlug)[0].investigation
                    .filter(i => i.slug === itemSlug)[0]
            }
        },
        getStartInvestigationBySlug({gettingStarted}){
            return (itemSlug) => {
                return gettingStarted.investigation
                    .filter(i => i.slug === itemSlug)[0]
            }
        },
        getDecisionsBySlug({actions}){
            return slug => actions
                .filter(a => a.slug === slug)[0]
                .decisions
        },
        currentSlug({actions}, {actionIndex}){
            if( actionIndex >= actions.length ){
                return "/complete"
            }
            return actions[actionIndex] ? "/sim/" + actions[actionIndex].slug : false
        },
        currentActionSelections({selected}, {actionIndex}){
            return (i, j) => selected[actionIndex][i][j]
        },
        areSelectionsMaxed({actions, selected}, {actionIndex}){
            return s => {
                const len = selected[actionIndex][s].filter(a => a).length
                const max = actions[actionIndex].decisions[s].typeMeta.count
                return len >= max
            }
        },
        actionFeedback({selected, actions}, {currentFeedback, actionIndex}){
            const idx = actionIndex - 1
            return actions[idx].decisions.map((d, i) => {
                return {
                    description : d.description,
                    response : d.response,
                    options : d.options.filter((a, j) => selected[idx][i][j]),
                    type: d.type
                }
            } )
        },
        actionFeedbackText({selected, actions}, {currentFeedback, actionIndex}){
            const idx = actionIndex - 1
            return actions[idx].feedback
        },
        // get the player's selections for each action
        playerSelections({selected, actions}){
            return actions.map((a, i) => {
                return a.decisions.map((d, j) => {
                    return d.options.filter((q, k) => {
                        return selected[i][j][k]
                    } )
                } )
            } )
        },
        playerSelectionCounts({dimensions}, {playerSelections}){
            const counts = {}
            Object.keys(dimensions).forEach( dk => {
                counts[dk] = {}
                Object.keys(dimensions[dk].options).map( opt => {
                    counts[dk][opt] = 0
                    playerSelections.forEach( action => {
                        action.forEach( decisions => {
                            decisions.forEach( option => {
                                const match = option.dimensions[dk] === opt
                                counts[dk][opt] += match ? 1 : 0
                            } )
                        } )
                    } )
                } )
            } )
            return counts
        },
        // get the player's scores for each selection of each action
        playerScores({dimensions}, {playerSelections}){
            const scores = {}
            Object.keys(dimensions).forEach( dk => {
                scores[dk] = playerSelections.map( actions => {
                    const act = actions.map( decisions => {
                        const dec = decisions.map( answer => {
                            const dim = answer.dimensions
                            const ans = dimensions[dk].options[dim[dk]]
                            return ans
                        } )
                        return dec
                    } )
                    return act
                } )
            } )
            return scores
        },
        // get the players total score
        playerTotal(state, {playerScores}){
            const totals = {}
            
            Object.keys(playerScores).forEach( dk => {
                totals[dk] = playerScores[ dk ]
                    .map( action => {
                        return action
                            .map( decision => {
                                const dec = decision.reduce( _reduce )
                                return dec
                            })
                            .reduce(_reduce)
                    } )
                    .reduce(_reduce)
            })
            return totals
        },
        // get a list of all the potential scores for each choice of actions.
        optionValues({actions, dimensions}){
            const counts = {}
            Object.keys(dimensions).forEach( dk => {
                counts[dk] = actions.map(({decisions}) => {
                    return decisions.map(({options}) => {
                        return options.map( opt => {
                            return dimensions[dk].options[opt.dimensions[dk]]
                        } )
                    } )
                } )
            } )
            return counts
        },
        // get the maximum potential scores
        // for each action sorted by dimension
        maxScores({actions}, {optionValues}){
            const sums = {}
            Object.keys(optionValues).forEach( dk => {
                sums[dk] = optionValues[dk].map((decision, i) => {
                    return decision.map((options, j) => {
                        const _c = actions[i].decisions[j].typeMeta.count
                        return options
                            .sort(_sort)
                            .reverse()
                            .slice(0, _c)
                            .reduce(_reduce)
                    } )
                } )
            } )
            return sums
        },
        maxTotal(state, {maxScores}){
            const totals = {}
            Object.keys(maxScores).forEach( dk => {
                totals[dk] = maxScores[dk]
                    .map( s => s.reduce(_reduce))
                    .reduce(_reduce)
            })
            // console.log( totals )
            return totals
        },
        getLeadershipBalance (state, {getScoring, playerTotal}){
            const vision = playerTotal.vision
            const tactics = playerTotal.tactics
            const horizon = Math.abs(vision - tactics)
            
            // orientation
            const market = playerTotal.market
            const organization = playerTotal.organization
            const orientation = Math.abs(market - organization)

            // mindset
            const enterprise = playerTotal.enterprise
            const team = playerTotal.team
            const mindset = Math.abs(enterprise - team)

            //approach
            const analytical = playerTotal.analytical
            const empathetic = playerTotal.empathetic
            const approach = Math.abs(analytical - empathetic)

            const balance = Math.round((horizon + orientation + mindset + approach) / 4)
            return balance
        },
        getNumberLineDataBalance (state, {getScoring, playerTotal}){
            
            let balanceArray = [
                { value: -playerTotal.vision, dataset: "a", label: "vision", placement: "l"},
                { value: playerTotal.tactics, dataset: "a", label: "tactics", placement: "r"},
                { value: -playerTotal.market, dataset: "b", label: "market", placement: "l"},
                { value: playerTotal.organization, dataset: "b", label: "organization", placement: "r"},
                { value: -playerTotal.enterprise, dataset: "c", label: "enterprise", placement: "l"},
                { value: playerTotal.team, dataset: "c", label: "team", placement: "r"},
                { value: -playerTotal.analytical, dataset: "d", label: "analytical", placement: "l"},
                { value: playerTotal.empathetic, dataset: "d", label: "empathetic", placement: "r"}
            ]
            return balanceArray.reverse() // reversed for d3

            // return object
        },
        getLeadershipMedal(state, {getScoring, getLeadershipBalance}){
            const medal = getScoring.medal.options
            const keys    = Object.keys( medal )
            // const vals    = playerSelectionCounts.medal
            const balance = getLeadershipBalance
            let cat       = medal[ keys[ 0 ] ]
            
            keys.forEach( key =>{
                
                const obj = medal[ key ]
                const conditions = obj.conditions
                const cKey = Object.keys( conditions )[0]
                // let should = true
                // console.log( cKey, conditions, balance)
                const min = conditions[cKey].min
                const max = conditions[cKey].max
                if (balance >= min && balance <= max) {
                    cat = obj
                }
                // cKeys.forEach( val => {
                //     should = should && (balance === conditions[val] )
                // } )

                // if( should ){
                //     cat = obj
                // }
            } )
            return cat
        },
        // getBalanceResult(state, {getScoring, playerSelectionCounts}){
        //     const balance = getScoring.balance.options
        //     const bkeys = Object.keys( balance )
        //     const _playerCounts = playerSelectionCounts.horizon
        //     let cat = "bronze"
        //     let _include = []

        //     // Karen's remap of 5 categories to 3 categories
        //     const playerCounts = {
        //         inner     : _playerCounts.distraction + _playerCounts.internal,
        //         moderate  : _playerCounts.industry,
        //         strategic : _playerCounts.external + _playerCounts.exploration
        //     }

        //     Object.keys( playerCounts ).forEach( dim => {
        //         const c = playerCounts[ dim ]
        //         if( c > 0 ){
        //             _include.push( dim )
        //         }
        //     } )

        //     console.log( _playerCounts )
        //     console.log( playerCounts )
        //     console.log( _include )

        //     const results = bkeys.map( k => {
        //         const c = balance[ k ].conditions
        //         // if the results don't have enough class representation
        //         // return false because it fails the condition
        //         if( c.include && c.include > _include.length ){
        //             return false
        //         }
        //         // if the included categories don't have
        //         // the number of required selections then it fails the condition
        //         if( c.count ){
        //             let countCondition = 0
        //             for( let i = 0; i < _include.length; i++ ){
        //                 // count for all included categories
        //                 countCondition += playerCounts[ _include[ i ] ] >= c.count ? 1 : 0
        //             }
        //             if( c.include > countCondition ){
        //                 return false
        //             }
        //         }
        //         if( c.exclude && _playerCounts[ c.exclude ] > 0 ){
        //             return false
        //         }
        //         return true
        //     } )

        //     results.forEach( ( r, i ) => {
        //         cat = r ? bkeys[ i ] : cat
        //     } )

        //     return cat
        // },
        // getBreadthResult(state, {getScoring, playerTotal}){
        //     const breadth = getScoring.breadth.options
        //     const pt = playerTotal.horizon
        //     let cat = "bronze"

        //     console.log( "breadth", pt )

        //     Object.keys( breadth ).forEach( k => {
        //         const min = breadth[ k ].conditions.range.min
        //         const max = breadth[ k ].conditions.range.max

        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )

        //     console.log( cat )
        //     return cat
        // },
        // getScopeResult(state, {getScoring, playerTotal}){
        //     const scope = getScoring.scope.options
        //     const pt = playerTotal.priority
        //     let cat = "bronze"
        //     Object.keys( scope ).forEach( k => {
        //         const min = scope[ k ].conditions.range.min
        //         const max = scope[ k ].conditions.range.max
        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )
        //     return cat
        // },
        // getBusinessResult(state, {getScoring, playerTotal}){
        //     const business = getScoring.business.options
        //     const pt = playerTotal.priority
        //     let cat = "bronze"
        //     Object.keys( business ).forEach( k => {
        //         const min = business[ k ].conditions.range.min
        //         const max = business[ k ].conditions.range.max
        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )
        //     return cat
        // },
        // getEffectivenessResult(state, {getScoring, playerTotal}){
        //     const effectiveness = getScoring.effectiveness.options
        //     const pt = playerTotal.effectiveness
        //     let cat = "bronze"
            
        //     Object.keys( effectiveness ).forEach( k => {
        //         const min = effectiveness[ k ].conditions.range.min
        //         const max = effectiveness[ k ].conditions.range.max
        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )
        //     return cat
        // },
        // getTimingResult(state, {getScoring, playerTotal}){
        //     const timing = getScoring.timing.options
        //     const pt = playerTotal.timing
            
        //     let cat = "delayed"
            
        //     Object.keys( timing ).forEach( k => {
        //         const min = timing[ k ].conditions.range.min
        //         const max = timing[ k ].conditions.range.max
        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )

        //     return timing[ cat ].description
        // },
        // getQualityResult(state, {getScoring, playerTotal}){
        //     const quality = getScoring.quality.options
        //     const pt = playerTotal.quality
            
        //     let cat = "poor"
            
        //     Object.keys( quality ).forEach( k => {
        //         const min = quality[ k ].conditions.range.min
        //         const max = quality[ k ].conditions.range.max
        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )

        //     return quality[ cat ].description
        // },
        // getInnovationResult(state, {getScoring, playerTotal}){
        //     const innovation = getScoring.innovation.options
        //     const pt = playerTotal.innovation
            
        //     let cat = "no"
            
        //     Object.keys( innovation ).forEach( k => {
        //         const min = innovation[ k ].conditions.range.min
        //         const max = innovation[ k ].conditions.range.max
        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )

        //     return innovation[ cat ].description
        // },
        // getCustomerResult(state, {getScoring, playerTotal}){
        //     const customer = getScoring.customer.options
        //     const pt = playerTotal.customer
            
        //     let cat = "no"
            
        //     Object.keys( customer ).forEach( k => {
        //         const min = customer[ k ].conditions.range.min
        //         const max = customer[ k ].conditions.range.max
        //         if( pt >= min && pt <= max ){
        //             cat = k
        //         }
        //     } )

        //     return customer[ cat ].description
        // },
        // getStakeholderMedal(state, {getScoring, playerSelectionCounts}){
        //     const primary = getScoring.primary.options
        //     const keys    = Object.keys( primary )
        //     const vals    = playerSelectionCounts.primary
        //     let cat = primary[ keys[ 0 ] ]

        //     keys.forEach( key =>{
        //         const obj = primary[ key ]
        //         const conditions = obj.conditions
        //         const cKeys = Object.keys( conditions )
        //         let should = true
        //         cKeys.forEach( val => {
        //             should = should && (vals[val] >= conditions[val] )
        //         } )
        //         if( should ){
        //             cat = obj
        //         }
        //     } )
        //     // console.log( vals, cat.label )

        //     return cat
        // },
        // getChartData(state, {getScoring, playerSelectionCounts}){
        //     const primary = getScoring.primary.options
        //     const keys    = Object.keys( primary )
        //     const vals    = playerSelectionCounts.primary

        //     delete vals["null"]


        //     console.log( vals )

        //     return vals
        // },
        // getOfferingResult(state, {getScoring, playerTotal}){
        //     const offering = getScoring.offering.options
        //     const keys = Object.keys( offering )
        //     let cat = "no"

        //     keys.forEach( k => {
        //         const conditions = offering[ k ].conditions
        //         const oKeys = Object.keys( conditions )
        //         let should = true
        //         if( oKeys.length <= 0 ){
        //             cat = offering[ k ]
        //         }else{
        //             oKeys.forEach( ok => {
        //                 const min = conditions[ ok ].min
        //                 const max = conditions[ ok ].max
        //                 const pt = playerTotal[ ok ]
        //                 should = should && ( pt >= min && pt <= max )
        //             } )
        //             if( should ){
        //                 cat = offering[ k ]
        //             }
        //         }
        //     } )
        //     return cat.description
        // },
        getStakeholderMedal(state, {getScoring, playerSelectionCounts}){
            const primary = getScoring.primary.options
            const keys    = Object.keys( primary )
            const vals    = playerSelectionCounts.primary
            let cat       = primary[ keys[ 0 ] ]
            keys.forEach( key =>{
                const obj = primary[ key ]
                const conditions = obj.conditions
                const cKeys = Object.keys( conditions )
                let should = true

                cKeys.forEach( val => {
                    should = should && (vals[val] === conditions[val] )
                } )

                if( should ){
                    cat = obj
                }
            } )
            return cat
        }
    }
})

export default store